import { useEffect, useRef, useState } from "react";

import { getSystemVoices, isSystemTtsAvailable, playSentenceAudio } from "./storyAudio";
import { getStoryVoiceId, saveStoryVoiceId } from "./storyStorage";

// Reads the story aloud one sentence at a time so the sentence being read can be highlighted.
export default function useStoryAudio({ sentences, languageIso, languageCode, onComplete }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [sentenceIndex, setSentenceIndex] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [voiceId, setVoiceId] = useState(getStoryVoiceId);
  const [, setVoicesVersion] = useState(0);
  const stopRef = useRef(() => {});
  const generationRef = useRef(0);
  const sentenceIndexRef = useRef(0);
  const speedRef = useRef(speed);
  speedRef.current = speed;
  const voiceIdRef = useRef(voiceId);
  voiceIdRef.current = voiceId;

  const hasServerAudio = sentences.some((sentence) => !!sentence.ttsUrl);
  const isAvailable = hasServerAudio || isSystemTtsAvailable(languageIso, languageCode);

  const stopCurrentSentence = () => {
    generationRef.current += 1;
    stopRef.current();
    stopRef.current = () => {};
  };

  const playFrom = (index, generation) => {
    if (generation !== generationRef.current) return;
    if (index >= sentences.length) {
      sentenceIndexRef.current = 0;
      setSentenceIndex(0);
      setIsPlaying(false);
      onComplete();
      return;
    }
    sentenceIndexRef.current = index;
    setSentenceIndex(index);
    stopRef.current = playSentenceAudio({
      sentence: sentences[index],
      rate: speedRef.current,
      languageIso,
      languageCode,
      voiceId: voiceIdRef.current,
      onEnded: () => playFrom(index + 1, generation),
      onError: () => generation === generationRef.current && pause()
    });
  };

  const play = () => {
    stopCurrentSentence();
    setIsPlaying(true);
    playFrom(sentenceIndexRef.current, generationRef.current);
  };

  const pause = () => {
    stopCurrentSentence();
    setIsPlaying(false);
  };

  const changeSpeed = (newSpeed) => {
    speedRef.current = newSpeed;
    setSpeed(newSpeed);
    if (isPlaying) play();
  };

  const changeVoice = (newVoiceId) => {
    voiceIdRef.current = newVoiceId;
    setVoiceId(newVoiceId);
    saveStoryVoiceId(newVoiceId);
    if (isPlaying) play();
  };

  const moveTo = (index) => {
    sentenceIndexRef.current = index;
    setSentenceIndex(index);
    if (isPlaying) play();
  };

  const rewind = () => moveTo(Math.max(sentenceIndexRef.current - 1, 0));

  const reset = () => {
    pause();
    sentenceIndexRef.current = 0;
    setSentenceIndex(0);
  };

  useEffect(() => {
    const refreshVoices = () => setVoicesVersion((version) => version + 1);
    window.speechSynthesis?.addEventListener?.("voiceschanged", refreshVoices);
    return () => {
      window.speechSynthesis?.removeEventListener?.("voiceschanged", refreshVoices);
      stopCurrentSentence();
    };
  }, []);

  const currentSentence = sentences[sentenceIndex];

  return {
    changeSpeed,
    changeVoice,
    isAvailable,
    isPlaying,
    pause,
    play,
    playingSentenceId: isPlaying ? currentSentence?.id : null,
    // Glide to the end of the current sentence over its estimated speaking time (~60ms per character at 1x), like the app.
    progressDurationMs: ((currentSentence?.text.length || 0) * 60) / speed,
    progressEnd: sentences.length ? (sentenceIndex + 1) / sentences.length : 0,
    progressStart: sentences.length ? sentenceIndex / sentences.length : 0,
    reset,
    rewind,
    sentenceIndex,
    speed,
    voiceId,
    voices: hasServerAudio ? [] : getSystemVoices(languageIso, languageCode)
  };
}
